/**
 * Feature Error Component
 *
 * A compact error fallback for individual features/routes that fail to load.
 * Used with Sentry.ErrorBoundary to isolate errors to specific features
 * without crashing the entire application.
 */

import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

interface FeatureErrorProps {
  name: string;
  error?: Error;
  resetError?: () => void;
}

export function FeatureError({ name, error, resetError }: FeatureErrorProps) {
  // Full page reload clears any corrupted state from the error
  const goToDashboard = () => {
    window.location.href = '/';
  };

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
        <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600" />
        </div>

        <h2 className="text-lg font-semibold text-gray-900 mb-2">Unable to load {name}</h2>

        <p className="text-sm text-gray-600 mb-6">
          Something went wrong while loading this feature. The error has been reported and our team
          is looking into it.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {resetError && (
            <button
              type="button"
              onClick={resetError}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#A66B32] rounded-md hover:bg-[#241610] focus:outline-none focus:ring-2 focus:ring-[#A66B32]/30 focus:ring-offset-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          )}

          <button
            type="button"
            onClick={goToDashboard}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#A66B32]/30 focus:ring-offset-2"
          >
            <Home className="w-4 h-4" />
            Go to Dashboard
          </button>
        </div>

        {import.meta.env.MODE === 'development' && error && (
          <div className="mt-6 p-3 bg-red-50 rounded-md text-left">
            <p className="text-xs font-medium text-red-800 mb-1">Error Details (Dev Only):</p>
            <pre className="text-xs text-red-700 overflow-auto max-h-32">{error.message}</pre>
          </div>
        )}
      </div>
    </div>
  );
}
